import isLoggedIn from 'helpers/isLoggedIn';
import getUserProfile from 'helpers/getUserProfile';
import isExternalUser from 'helpers/isExternalUser';
import { RootState } from 'redux/rootReducer';
import { Dispatch } from 'redux';
import { RSAA } from 'redux-api-middleware';
import { TGetRootState } from '../../types/shared';
import { hasExternalViewLabelsGroup } from '../../helpers/hasExternalViewLabelsGroup';

export const SET_AUTHENTICATION_STATUS =
  'opus/authentication/SET_AUTHENTICATION_STATUS';
export const GET_AUTHENTICATION_LABEL_STATUS_REQUEST =
  'opus/authentication/GET_AUTHENTICATION_LABEL_STATUS_REQUEST';
export const GET_AUTHENTICATION_LABEL_STATUS_SUCCESS =
  'opus/authentication/GET_AUTHENTICATION_LABEL_STATUS_SUCCESS';
export const GET_AUTHENTICATION_LABEL_STATUS_FAILURE =
  'opus/authentication/GET_AUTHENTICATION_LABEL_STATUS_FAILURE';

export interface IAuthenticationProfile {
  id: string;
  email: string;
  name: string;
  groups: string[];
  isExternal: boolean;
}

interface IAuthenticationState {
  isAuthenticated: boolean;
  profile: IAuthenticationProfile;
  isExternalUser: boolean;
  isExternalOneLabel: boolean;
  externalLoaded: boolean;
  externalLabels: string[];
}

interface IAuthenticationAction {
  type?: typeof SET_AUTHENTICATION_STATUS;
  isAuthenticated?: boolean;
}

/**
 * @todo REMOVE TESTING BOOLEAN FOR isExternalUser
 */

export const initialState: IAuthenticationState = {
  isAuthenticated: isLoggedIn(),
  profile: getUserProfile(),
  isExternalUser: isExternalUser(getUserProfile()),
  // External users cannot access the Labels view until their label visibility is resolved.
  isExternalOneLabel: isExternalUser(getUserProfile()),
  externalLabels: [],
  // Only external users with the Labels permission group require an additional visibility check.
  externalLoaded:
    isExternalUser(getUserProfile()) &&
    hasExternalViewLabelsGroup(getUserProfile())
      ? false
      : true,
};

export default function reducer(
  state: IAuthenticationState = initialState,
  action: IAuthenticationAction = {}
): IAuthenticationState {
  switch (action.type) {
    case SET_AUTHENTICATION_STATUS:
      return {
        ...state,
        isAuthenticated: Boolean(action.isAuthenticated),
      };
    case GET_AUTHENTICATION_LABEL_STATUS_SUCCESS:
      return {
        ...state,
        // External users need access to at least two labels to use the Labels view.
        isExternalOneLabel: (action.payload?.labels ?? []).length < 2,
        externalLabels: [
          ...state.externalLabels,
          ...action.payload.labels.map(
            ({ label }: { label: string }) => label?.id
          ),
        ],
        externalLoaded: true,
      };
    case GET_AUTHENTICATION_LABEL_STATUS_FAILURE:
      return {
        ...state,
        externalLoaded: true,
      };
    default:
      return state;
  }
}

export function checkAuthenticationStatus(): IAuthenticationAction {
  return {
    type: SET_AUTHENTICATION_STATUS,
    isAuthenticated: isLoggedIn(),
  };
}

/** Gets the count of the label allowed to see the user. */
export const getExternalLabelVisibility = (): any => {
  return async function (dispatch: Dispatch): Promise<any> {
    return await dispatch({
      [RSAA]: {
        types: [
          { type: GET_AUTHENTICATION_LABEL_STATUS_REQUEST },
          { type: GET_AUTHENTICATION_LABEL_STATUS_SUCCESS },
          { type: GET_AUTHENTICATION_LABEL_STATUS_FAILURE },
        ],
        endpoint: '/api/browse/labels?limit=20',
        method: 'GET',
        credentials: 'same-origin',
      },
    });
  };
};

export function checkExternalLabelVisibilityStatus(): IAuthenticationAction {
  return async function (
    dispatch: Dispatch,
    getState: TGetRootState
  ): Promise<any> {
    const { isExternalUser, profile } = getState().authentication;

    // Fetch label visibility only for external users allowed to access multiple labels
    return isExternalUser && hasExternalViewLabelsGroup(profile)
      ? await dispatch(getExternalLabelVisibility())
      : {};
  };
}

export function checkIsExternalUser(state: RootState): boolean {
  return state.authentication.isExternalUser;
}

export function checkIsExternalOneLabelUser(state: RootState): boolean {
  return state.authentication.isExternalOneLabel;
}

export const selectUserId = (state: RootState): string | undefined =>
  state.authentication?.profile?.id;
